import { CampaignCard } from '@/components/CampaignCardVertical';
import GridCampaignCard from '@/components/layout/GridCampaignCard';
import { cn } from '@/utils/utils';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { Fragment, useMemo, useState } from 'react';

interface AvailableCampaignsProps {
  campaigns: CampaignDto[];
  header?: {
    title: string;
    icon: string;
  };
  type: 'play_now' | 'scheduled' | 'default';
  handlePlayNow: (campaignUUid: string) => void;
  handleAddScheduleCampaign: (campaign: CampaignDto) => void;
  handleSetDefaultCampaign: (campaign: CampaignDto) => void;
}

export function AvailableCampaigns({
  campaigns,
  header,
  type,
  handlePlayNow,
  handleAddScheduleCampaign,
  handleSetDefaultCampaign,
}: AvailableCampaignsProps) {
  const groupedCampaigns = useMemo(() => {
    return campaigns.reduce(
      (acc, campaign) => {
        acc[campaign.brand.name] = acc[campaign.brand.name] || [];
        acc[campaign.brand.name].push(campaign);
        return acc;
      },
      {} as Record<string, CampaignDto[]>
    );
  }, [campaigns]);

  function CampaignCardWrapper({ campaign }: { campaign: CampaignDto }) {
    const [showModal, setShowModal] = useState(false);
    return (
      <CampaignCard
        campaign={campaign}
        key={campaign.uuid}
        showSetPlayNow={type === 'play_now' && showModal}
        showSetSchedule={type === 'scheduled' && showModal}
        showSetAsDefault={type === 'default' && showModal}
        actions={{
          onClick: () => setShowModal(true),
          onCloseModal: () => setShowModal(false),
          onSetPlayNow: () => handlePlayNow(campaign.uuid),
          onSetAsDefault: () => handleSetDefaultCampaign(campaign),
          onSetSchedule: () => handleAddScheduleCampaign(campaign),
        }}
      />
    );
  }

  return (
    <div className="flex flex-col items-start overflow-x-hidden">
      {header && (
        <div className="flex flex-row items-center gap-2">
          <Icon name={header.icon} fill={1} size={22} />
          <h3 className="text-lg leading-5 font-semibold sm:text-xl">{header.title}</h3>
        </div>
      )}
      {Object.entries(groupedCampaigns).length > 0 ? (
        <div className="w-full sm:px-0">
          {Object.entries(groupedCampaigns)
            .sort((a, b) => a[1][0].brand.name.localeCompare(b[1][0].brand.name))
            .map(([brand, campaigns]) => (
              <Fragment key={brand}>
                <div className="mt-6 flex items-center gap-2 text-xl font-semibold">
                  <div className="h-6 w-2 rounded-full bg-[var(--primary)]"></div>
                  <h3 className="text-md leading-5 font-semibold sm:text-xl">{brand}</h3>
                </div>

                <div className="md:hidden">
                  <div className="scrollbar-hide flex min-h-32 w-full overflow-x-auto">
                    <div className="flex gap-2 py-2">
                      {campaigns
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((campaign, index) => (
                          <div
                            key={campaign.uuid}
                            className={cn(
                              'w-[120px] flex-shrink-0',
                              index === 0 && 'ml-2',
                              index === campaigns.length - 1 && 'mr-2'
                            )}
                          >
                            <CampaignCardWrapper key={campaign.uuid} campaign={campaign} />
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Grid para desktop/tablet */}
                <div className="hidden md:block">
                  <GridCampaignCard>
                    {campaigns.map((campaign) => (
                      <CampaignCardWrapper key={campaign.uuid} campaign={campaign} />
                    ))}
                  </GridCampaignCard>
                </div>
              </Fragment>
            ))}
        </div>
      ) : (
        <div className="flex min-h-32 w-full items-center justify-center">
          <p className="text-sm text-gray-500">Nenhuma campanha disponível</p>
        </div>
      )}
    </div>
  );
}
